import type { EntityColumn } from './AdminEntityList';

const EM_DASH = '—';

/* Derived payor signals, shown when the source declares nothing. Wording is
   deliberately hedged -- "UMED pathway", not "Payor: UMED" -- because these
   are observations about the patient's workflow, not a payor category the
   business uses. */
const SIGNAL_LABELS: Record<string, string> = {
  UMED_PATHWAY: 'UMED pathway',
  RESPONSIBLE_PARTY_ON_RECORD: 'Resp. party',
  RESPONSIBLE_PARTY_CONTACTABLE: 'contactable',
  PATIENT_LIABILITY_SET: 'Liability set',
  PAYOR_ISSUES_FLAGGED: 'Payor issue',
  POA_ON_RECORD: 'POA',
};

// Most informative first, so a truncated cell still says something useful.
const SIGNAL_LABEL_ORDER = [
  'UMED_PATHWAY',
  'RESPONSIBLE_PARTY_ON_RECORD',
  'RESPONSIBLE_PARTY_CONTACTABLE',
  'PAYOR_ISSUES_FLAGGED',
  'POA_ON_RECORD',
  'PATIENT_LIABILITY_SET',
];

// Column sets for the eMerge admin entity list/detail pages. Kept separate
// from AdminEntityList so the generic component stays entity-agnostic.
export const ENTITY_COLUMNS: Record<string, EntityColumn[]> = {
  facilities: [
    { key: 'name', label: 'Name' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'status', label: 'Status' },
    { key: 'licensed_beds', label: 'Licensed Beds' },
    { key: 'region', label: 'Region' },
  ],
  patients: [
    { key: 'last_name', label: 'Last Name' },
    { key: 'first_name', label: 'First Name' },
    { key: 'status', label: 'Status' },
    { key: 'phase', label: 'Phase' },
    {
      key: 'payor_source',
      label: 'Payor',
      /* Monday's Payor Source field is set for 3.6% of patients, so this
         column read as empty for almost everyone. What the source declares
         still wins and is shown verbatim; when it says nothing, the derived
         signals staff actually maintain are shown instead, clearly marked as
         inference rather than dressed up as a declaration. */
      format: (value: any, row: any) => {
        if (value !== null && value !== undefined && String(value).trim() !== '') return String(value);
        const ctx = row?.payor_context;
        if (!ctx || !ctx.signals?.length) return EM_DASH;
        return SIGNAL_LABEL_ORDER
          .filter((s) => ctx.signals.includes(s))
          .map((s) => SIGNAL_LABELS[s])
          .join(' · ');
      },
    },
    { key: 'city', label: 'City' },
  ],
  scheduling: [
    { key: 'facility_id', label: 'Facility ID' },
    { key: 'status', label: 'Status' },
    { key: 'date_of_last_visit', label: 'Last Visit' },
    { key: 'tentative_next_visit', label: 'Next Visit' },
  ],
  completed_visits: [
    { key: 'scheduling_id', label: 'Scheduling ID' },
    { key: 'visit_date', label: 'Visit Date' },
  ],
  hearing_aids: [
    { key: 'serial_number', label: 'Serial Number' },
    { key: 'hearing_aid_type', label: 'Type' },
    { key: 'ear', label: 'Ear' },
    { key: 'status', label: 'Status' },
    { key: 'delivery_date', label: 'Delivery Date' },
  ],
  patient_qualification: [
    { key: 'facility_name_raw', label: 'Facility' },
    { key: 'priority', label: 'Priority' },
    { key: 'needs_verification', label: 'Needs Verification' },
  ],
  patient_advocate_leads: [
    { key: 'responsible_party_name', label: 'Responsible Party' },
    { key: 'status', label: 'Status' },
    { key: 'file_status', label: 'File Status' },
    { key: 'sfs_rep', label: 'SFS Rep' },
    { key: 'date_assigned', label: 'Date Assigned' },
  ],
  finance_accounts: [
    { key: 'status', label: 'Status' },
    { key: 'corporate_name', label: 'Corporate Name' },
    { key: 'total_sale_value', label: 'Total Sale Value' },
    { key: 'remaining_amount', label: 'Remaining' },
    { key: 'amount_overdue', label: 'Overdue' },
  ],
  invoices: [
    { key: 'invoice_number', label: 'Invoice #' },
    { key: 'status', label: 'Status' },
    { key: 'invoice_total', label: 'Total' },
    { key: 'amount_paid', label: 'Paid' },
    { key: 'invoice_due_date', label: 'Due Date' },
  ],
  payments: [
    { key: 'processor', label: 'Processor' },
    { key: 'transaction_type', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'amount', label: 'Amount' },
    { key: 'currency', label: 'Currency' },
    { key: 'processed_at', label: 'Processed At' },
  ],
};

export const ENTITY_TITLES: Record<string, string> = {
  facilities: 'Facilities',
  patients: 'Patients',
  scheduling: 'Scheduling',
  completed_visits: 'Completed Visits',
  hearing_aids: 'Hearing Aids',
  patient_qualification: 'Patient Qualification',
  patient_advocate_leads: 'Advocate Leads',
  finance_accounts: 'Finance Accounts',
  invoices: 'Invoices',
  payments: 'Payments',
};
